import { FC, ReactElement, CSSProperties, useEffect, useState, useRef, useCallback } from 'react';

import Buttons from 'components/Buttons';
import ProgressIndicator from 'components/ProgressIndicator';
import classNames from 'classnames';
import XCircle from '@components/Icons/XCircle';
import { m } from 'framer-motion';
import isInIframe from '@helpers/isInIframe';
import isAndroid from '@helpers/isAndroid';
import Spacer from '@components/Spacer';
import s from './Modal.module.scss';
import Portal from '../Portal';

type Props = {
  onClose?: () => void;
  onIconClick?: () => void;
  title?: string;
  isFetching?: boolean;
  hasCloseOnActions?: boolean;
  hasForm?: boolean;
  isFullScreen?: boolean;
  isPinnedToBottom?: boolean;
  actions?: Array<ReactElement>;
  isSideSticky?: boolean;
  hasPadding?: boolean;
  hasUnsavedChanges?: boolean;
  icon?: ReactElement;
  containerStyle?: CSSProperties;
  zIndex?: number;
  hideBottomInset?: boolean;
};

const Modal: FC<Props> = ({
  onClose,
  onIconClick,
  icon = <XCircle weight="fill" size={24} color='var(--color-gray-100)' />,
  title,
  children,
  isFetching = false,
  actions = [],
  hasCloseOnActions = true,
  hasForm: hasFormExternal,
  hasUnsavedChanges = false,
  isFullScreen = false,
  isPinnedToBottom = false,
  isSideSticky = false,
  hasPadding = false,
  containerStyle = {},
  zIndex,
  hideBottomInset,
}) => {

  const [hasForm, setHasForm] = useState(hasFormExternal ?? false);
  const [isIconVisible, setIsIconVisible] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onIconClickRef = useRef(onIconClick);
  onIconClickRef.current = onIconClick;

  const modalActions = [
    ...actions,
    ...(hasCloseOnActions && onClose
      ? [
        <Buttons key="close" isFlat onClick={onClose}>
          Close
        </Buttons>,
      ]
      : []),
  ];

  const style: CSSProperties = {};
  let containerStyleOverrides: CSSProperties = {};

  if (isFullScreen) {
    style.maxWidth = '100vw';
    style.maxHeight = 'min(100vh, var(--host-height))';
    containerStyleOverrides = {
      ...style,
      ...containerStyle,
    };
  }

  if (isPinnedToBottom && !isInIframe()) {
    containerStyleOverrides.position = 'fixed';
    containerStyleOverrides.bottom = '0';
    containerStyleOverrides.width = '100vw';
    containerStyleOverrides.maxWidth = '100vw';
    containerStyleOverrides.borderBottomLeftRadius = '0';
    containerStyleOverrides.borderBottomRightRadius = '0';
  }

  useEffect(() => {
    setIsIconVisible(Boolean(onClose || onIconClick));
  }, [onClose, onIconClick]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (hasFormExternal === undefined) {
      const modalBody = document.getElementById('modal-body');
      if (modalBody) {
        const label = modalBody.querySelector('label');
        setHasForm(!!label);
      }
    } else {
      setHasForm(hasFormExternal);
    }
  }, [hasFormExternal]);

  // trap focus in modal
  useEffect(() => {
    const elementsToSelect = '#dialog a[href]:not([disabled]), #dialog button:not([disabled]), #dialog textarea:not([disabled]), #dialog input[type="text"]:not([disabled]), #dialog input[type="radio"]:not([disabled]), #dialog input[type="checkbox"]:not([disabled]), #dialog select:not([disabled])';

    const firstFocusableElement = document.querySelector(elementsToSelect);

    const FocusableHTMLElements = HTMLButtonElement || HTMLAnchorElement || HTMLInputElement || HTMLSelectElement || HTMLTextAreaElement;

    if (firstFocusableElement instanceof FocusableHTMLElements) {
      firstFocusableElement.focus();
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (onCloseRef.current) {
          onCloseRef.current();
        }
        if (onIconClickRef.current) {
          onIconClickRef.current();
        }
      }
      if (event.key === 'Tab') {
        const focusableElements = document.querySelectorAll(elementsToSelect);

        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement && lastFocusableElement instanceof FocusableHTMLElements) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          if (firstFocusableElement instanceof FocusableHTMLElements) {
            firstFocusableElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleClickMask: React.MouseEventHandler<HTMLDivElement> = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (hasUnsavedChanges || hasForm) {
        // eslint-disable-next-line no-alert
        if (window.confirm('Are you sure you want to close this modal? Changes you made may not be saved.')) {
          if (onCloseRef.current) {
            onCloseRef.current();
          }
          if (onIconClickRef.current) {
            onIconClickRef.current();
          }
        }
      } else {
        if (onCloseRef.current) {
          onCloseRef.current();
        }
        if (onIconClickRef.current) {
          onIconClickRef.current();
        }
      }
    }
  }, [hasForm, hasUnsavedChanges]);

  const handleClickIcon: React.MouseEventHandler<HTMLButtonElement> = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    if (onCloseRef.current) {
      onCloseRef.current();
    } else if (onIconClickRef.current) {
      onIconClickRef.current();
    }
  }, []);

  return (
    <Portal>
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.2,
        }}
        className={classNames(s.modal__backdrop, {
          [s['modal__backdrop--side-sticky']]: isSideSticky,
        })}
        style={{ ...(isInIframe() && { alignItems: 'flex-start' || undefined, paddingTop: 24 }), zIndex }}
        id="modal-backdrop"
        onClick={handleClickMask}
      >
        <m.div
          id="dialog"
          initial={{ y: 40 }}
          animate={{ y: 0 }}
          className={classNames(s.modal__container, {
            [s['modal__container--side-sticky']]: isSideSticky,
            [s['modal__container--has-padding']]: hasPadding,
          })}
          style={containerStyleOverrides}
        >
          {isFetching && <ProgressIndicator isAbsolute />}
          {title && (
            <div className={s.header}>
              {isIconVisible && (
                <button className={s.closeButton} onClick={handleClickIcon}>
                  {icon}
                </button>
              )}
              <span className="body-sm">{title}</span>
            </div>
          )}
          <m.div
            id="modal-body"
            layoutScroll
            className={classNames(s.modal__body, {
              [s['modal__body--side-sticky']]: isSideSticky,
            })}
            style={style}
          >
            {title && <Spacer size="24px" />}
            {children}
            {((isPinnedToBottom && isAndroid()) || (isPinnedToBottom && !hideBottomInset && !isInIframe())) && <Spacer size="60px" />}
          </m.div>
          {modalActions.length > 0 && <div className={s.modal__actions}>{modalActions}</div>}
        </m.div>
      </m.div>
    </Portal>
  );
};

export default Modal;
